import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { formatIQD } from "../lib/format";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  UtensilsCrossed, DollarSign, TrendingUp, Users, AlertCircle,
  Plus, Wallet, UserPlus, FileBarChart, ArrowRight,
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";

const BAR_RADIUS = [6, 6, 0, 0];
const H_BAR_RADIUS = [0, 6, 6, 0];
const KTICK = (v) => (v / 1000).toFixed(0) + "k";
const DATE_TICK = (d) => d?.slice(5);

const KpiCard = ({ label, value, hint, icon: Icon, tone = "slate", testid }) => {
  const tones = {
    emerald: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40",
    rose: "text-rose-600 bg-rose-50 dark:bg-rose-950/40",
    amber: "text-amber-600 bg-amber-50 dark:bg-amber-950/40",
    blue: "text-blue-600 bg-blue-50 dark:bg-blue-950/40",
    slate: "text-slate-600 bg-slate-100 dark:bg-slate-800",
  };
  return (
    <Card className="kpi-card p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{label}</div>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tones[tone]}`}>
          <Icon size={17} />
        </div>
      </div>
      <div className="mt-3 heading text-2xl font-extrabold tracking-tight">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </Card>
  );
};

const QuickAction = ({ label, icon: Icon, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="group flex items-center gap-3 rounded-xl border border-border bg-white dark:bg-slate-900 p-4 text-left transition hover:border-emerald-500 hover:shadow-md w-full"
  >
    <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 flex items-center justify-center">
      <Icon size={18} />
    </div>
    <div className="flex-1">
      <div className="font-medium text-sm">{label}</div>
    </div>
    <ArrowRight size={16} className="text-muted-foreground group-hover:text-emerald-600 transition" />
  </button>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/dashboard/charts").then((r) => setCharts(r.data));
  }, []);

  const t = stats?.today || {};
  const o = stats?.overall || {};

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="heading text-2xl font-extrabold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Real-time overview of today's orders, payments and outstanding balances.</p>
      </div>

      {/* Today */}
      <div>
        <div className="text-xs uppercase font-semibold text-muted-foreground mb-2 tracking-wider">Today</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard testid="kpi-today-orders" label="Today's Orders" value={t.orders_count ?? "—"} hint="Employees who ordered today" icon={UtensilsCrossed} tone="emerald" />
          <KpiCard testid="kpi-today-cost" label="Today's Food Cost" value={formatIQD(t.food_cost)} hint="Total value of today's orders" icon={DollarSign} tone="blue" />
          <KpiCard testid="kpi-today-payments" label="Today's Payments" value={formatIQD(t.payments_collected)} hint="Collected from employees" icon={Wallet} tone="emerald" />
          <KpiCard testid="kpi-today-new-outstanding" label="Today's New Outstanding" value={formatIQD(t.new_outstanding)} hint="Unpaid portion of today's orders" icon={AlertCircle} tone="rose" />
        </div>
      </div>

      {/* Overall */}
      <div>
        <div className="text-xs uppercase font-semibold text-muted-foreground mb-2 tracking-wider">Overall</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard testid="kpi-total-outstanding" label="Total Outstanding" value={formatIQD(o.total_outstanding)} hint="Owed by all employees" icon={AlertCircle} tone="rose" />
          <KpiCard testid="kpi-active-employees" label="Active Employees" value={o.active_employees ?? "—"} hint="Available for daily orders" icon={Users} tone="slate" />
          <KpiCard testid="kpi-month-cost" label="This Month Food Cost" value={formatIQD(o.month_food_cost)} hint="Current month total" icon={TrendingUp} tone="blue" />
          <KpiCard testid="kpi-month-payments" label="This Month Payments" value={formatIQD(o.month_payments)} hint="Collected this month" icon={Wallet} tone="emerald" />
        </div>
      </div>

      {/* Quick actions */}
      <div>
        <div className="text-xs uppercase font-semibold text-muted-foreground mb-2 tracking-wider">Quick Actions</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <QuickAction testid="qa-create-order" label="Create Today's Order" icon={Plus} onClick={() => navigate("/orders?new=1")} />
          <QuickAction testid="qa-record-payment" label="Record Payment" icon={Wallet} onClick={() => navigate("/payments")} />
          <QuickAction testid="qa-add-employee" label="Add Employee" icon={UserPlus} onClick={() => navigate("/employees?new=1")} />
          <QuickAction testid="qa-view-outstanding" label="View Outstanding" icon={AlertCircle} onClick={() => navigate("/outstanding")} />
          <QuickAction testid="qa-monthly-report" label="Monthly Report" icon={FileBarChart} onClick={() => navigate("/reports")} />
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5" data-testid="chart-daily-cost">
          <div className="mb-3">
            <div className="heading font-bold">Daily Food Cost — Current Month</div>
            <div className="text-xs text-muted-foreground">Amount ordered by day (IQD)</div>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={charts?.daily || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" fontSize={11} tickFormatter={DATE_TICK} />
                <YAxis fontSize={11} tickFormatter={KTICK} />
                <Tooltip formatter={(v) => formatIQD(v)} />
                <Bar dataKey="food_cost" fill="hsl(160 84% 39%)" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5" data-testid="chart-daily-payments">
          <div className="mb-3">
            <div className="heading font-bold">Payments Collected by Day</div>
            <div className="text-xs text-muted-foreground">Current month, IQD collected</div>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <LineChart data={charts?.daily || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" fontSize={11} tickFormatter={DATE_TICK} />
                <YAxis fontSize={11} tickFormatter={KTICK} />
                <Tooltip formatter={(v) => formatIQD(v)} />
                <Line type="monotone" dataKey="payments" stroke="hsl(217 91% 60%)" strokeWidth={3} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 lg:col-span-2" data-testid="chart-top-outstanding">
          <div className="mb-3">
            <div className="heading font-bold">Top Outstanding Employees</div>
            <div className="text-xs text-muted-foreground">Employees currently owing the most</div>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={charts?.top_outstanding || []} layout="vertical" margin={{ left: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" fontSize={11} tickFormatter={KTICK} />
                <YAxis type="category" dataKey="name" fontSize={11} width={100} />
                <Tooltip formatter={(v) => formatIQD(v)} />
                <Bar dataKey="outstanding" fill="hsl(0 72% 51%)" radius={H_BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}
